// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  dataUrlToBlobUrl,
  cvFetchInit,
} from "@/legacy/helpersModule";
import { TalentPoolPdfViewer } from "@/features/talent-pool/components/TalentPoolPdfViewer";
export function ResumePreviewModal({ onClose, dataUrl, fetchUrl, fileName, ext, mime, cvText }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [remoteDataUrl, setRemoteDataUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  /* Talent-pool CVs live in S3, so there is always a round trip before the file
   * exists locally. Without this the component fell through to the "Word
   * document" branch while fetching, which looked like PDF preview was broken. */
  const [loading, setLoading] = useState(Boolean(fetchUrl && !dataUrl));
  useEffect(() => {
    let revoke = null;
    setLoadError(null);
    if (dataUrl) {
      const u = dataUrlToBlobUrl(dataUrl);
      setBlobUrl(u);
      setRemoteDataUrl(null);
      setLoading(false);
      revoke = u;
      return () => { if (revoke) URL.revokeObjectURL(revoke); };
    }
    if (fetchUrl) {
      let cancelled = false;
      setLoading(true);
      fetch(fetchUrl, cvFetchInit(fetchUrl))
        .then((r) => {
          if (!r.ok) throw new Error(`Could not load file (${r.status})`);
          return r.blob();
        })
        .then((blob) => {
          if (cancelled) return;
          const u = URL.createObjectURL(blob);
          setBlobUrl(u);
          setLoading(false);
          revoke = u;
          const reader = new FileReader();
          reader.onload = () => {
            if (!cancelled) setRemoteDataUrl(reader.result);
          };
          reader.readAsDataURL(blob);
        })
        .catch((err) => {
          if (cancelled) return;
          setLoading(false);
          setLoadError(String(err?.message || err || "Failed to load resume"));
        });
      return () => {
        cancelled = true;
        if (revoke) URL.revokeObjectURL(revoke);
      };
    }
    setBlobUrl(null);
    setRemoteDataUrl(null);
    setLoading(false);
  }, [dataUrl, fetchUrl]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose?.(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  const ex = (ext || "").toLowerCase();
  const mt = (mime || "").toLowerCase();
  const effectiveDataUrl = dataUrl || remoteDataUrl;
  /* Trust the stored MIME type as well as the extension — an entry whose
   * filename lost its suffix still previews correctly. */
  const isPdf =
    ex === "pdf" ||
    mt === "application/pdf" ||
    (effectiveDataUrl && effectiveDataUrl.indexOf("data:application/pdf") === 0);
  const isImage =
    ["jpg", "jpeg", "png", "gif", "webp"].includes(ex) ||
    mt.startsWith("image/") ||
    (effectiveDataUrl && effectiveDataUrl.indexOf("data:image/") === 0);
  const src = blobUrl || effectiveDataUrl;
  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 safe-x" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={fileName || "Resume"} className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90dvh] sm:max-h-[92vh] flex flex-col overflow-hidden safe-bottom" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-line shrink-0 safe-top">
          <h2 className="font-bold text-ink-900 truncate pr-6 text-sm sm:text-base">{fileName || "Resume"}</h2>
          <button type="button" onClick={onClose} aria-label="Close preview" className="text-ink-400 hover:text-ink-600 w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 shrink-0">✕</button>
        </div>
        <div className="flex-1 overflow-auto hide-scrollbar bg-slate-100 p-2 sm:p-3 min-h-[40dvh] sm:min-h-[45vh]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
              <p className="text-sm text-ink-400">Loading preview…</p>
            </div>
          ) : isPdf && src ? (
            <TalentPoolPdfViewer blobUrl={blobUrl} dataUrl={effectiveDataUrl} />
          ) : isImage && src ? (
            <div className="flex justify-center">
              <img src={src} alt={`Resume preview — ${fileName || "candidate document"}`} className="max-w-full max-h-[82vh] object-contain rounded-lg shadow bg-white" />
            </div>
          ) : loadError ? (
            <div className="p-6 text-center">
              <p className="text-sm text-red-600 mb-2">{loadError}</p>
              <p className="text-sm text-ink-400">Try Download again after refreshing the page, or contact HR if the issue persists.</p>
            </div>
          ) : (
            <div className="p-4">
              <p className="text-sm text-ink-600 mb-3">In-app preview works best for PDF and images. For Word documents, use the extracted text below or Download.</p>
              {cvText ? (
                <pre className="whitespace-pre-wrap font-sans text-xs bg-white p-4 rounded-xl border border-line max-h-[72vh] overflow-auto hide-scrollbar text-ink-900">{cvText}</pre>
              ) : (
                <p className="text-sm text-ink-400">No preview available. Use Download.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
